// Лента прокручивается к последнему сообщению. SMY-76
//
// Требование дизайна («Клавиатура»): новое сообщение должно оказаться на
// виду само. Без этого лента на экранах 03 и 05 растёт вниз, а человек
// продолжает смотреть на начало — и решает, что ничего не происходит.
//
// Прокрутка ТОЛЬКО когда человек и так внизу. Утащить его к концу в тот
// момент, когда он отлистал наверх перечитать вопрос, — не помощь, а отъём
// управления; так ведёт себя чат, из которого невозможно читать историю.
//
// ИСКЛЮЧЕНИЕ — плашка-предупреждение о коде из СМС и данных карты живого
// запуска (SMY-551, ворота T6). Она встаёт на виду один раз, даже если человек
// отлистал вверх, и снимает «держать низ» (`held`), пока идёт тот же запуск,
// до следующей своей отправки или смены чата: иначе поток ответа или
// `after_answer`, пришедшая после него, уводили плашку выше экрана. То же
// правило в приложении — `apps/mobile/src/screens/Chat.tsx` (`warnedRun`).

import { useEffect, useRef } from 'react'

/** Насколько близко к низу считается «человек смотрит конец». */
const NEAR_BOTTOM = 80

/**
 * Держать конец ленты на виду. Возвращает ref для контейнера с прокруткой.
 *
 * `deps` — то, от появления чего лента растёт: список сообщений, этапов,
 * источников. Пустой массив означал бы «прокрутить один раз при открытии»,
 * а нужно при каждом добавлении.
 */
export function useScrollToEnd<T extends HTMLElement>(
  deps: readonly unknown[],
  held?: { readonly current: boolean },
): React.RefObject<T | null> {
  const ref = useRef<T>(null)
  // Был ли человек внизу ДО того, как содержимое выросло. После роста
  // измерять поздно: расстояние до низа уже другое.
  const wasAtEnd = useRef(true)

  useEffect(() => {
    const el = ref.current
    if (el == null) return
    const onScroll = (): void => {
      wasAtEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (el == null || !wasAtEnd.current || held?.current === true) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    // Зависимости задаёт вызывающий — он и знает, от чего лента растёт.
  }, deps)

  return ref
}
